import React, { useCallback, useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getEventByToken, joinEvent } from "../../services/eventService";
import "./InvitationAccept.css";

const EMPTY_VALUE = "—";
const INVITATION_REDIRECT_PATH = "events/invitation/accept";
const PENDING_INVITATION_RESPONSE_KEY = "pendingInvitationResponse";

const RESPONSE_STATE_BY_STATUS = {
  accepted: "accepted",
  maybe: "declined",
  rejected: "rejected",
};

const RESPONSE_ERROR_BY_STATUS = {
  accepted: "Failed to accept invitation. Please try again.",
  maybe: "Failed to save your response. Please try again.",
  rejected: "Failed to save your response. Please try again.",
};

const getFirstPresent = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getDisplayValue = (...values) =>
  getFirstPresent(...values) ?? EMPTY_VALUE;

const formatEventDate = (eventData) => {
  if (
    eventData.event_date_day &&
    eventData.event_date_date &&
    eventData.event_date_month &&
    eventData.event_date_year
  ) {
    return `${eventData.event_date_day}, ${eventData.event_date_date} ${eventData.event_date_month} ${eventData.event_date_year}`;
  }

  return getFirstPresent(eventData.event_date, eventData.date) || "";
};

const formatEventTime = (value) => {
  if (!value) {
    return "";
  }

  const [hours, minutes] = value.split(":").map(Number);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return value;
  }

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  const detail = responseData?.detail;

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const detailMessage = detail
      .map((item) => (typeof item === "string" ? item : item?.msg))
      .filter(Boolean)
      .join(" ");

    if (detailMessage) {
      return detailMessage;
    }
  }

  return (
    responseData?.message ||
    responseData?.error?.message ||
    (typeof responseData?.error === "string" ? responseData.error : "") ||
    fallbackMessage
  );
};

const InvitationAccept = () => {
  const { event_code } = useParams();
  const eventCode = event_code;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [eventDetails, setEventDetails] = useState(null);
  const isEventCompleted = eventDetails?.isCompleted === true;
  const [accepting, setAccepting] = useState(false);
  const [responseState, setResponseState] = useState(null); // 'accepted', 'declined', 'rejected', or null
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const pendingResponseAttemptedRef = useRef(false);
  const navigate = useNavigate();

  const fetchEventDetails = useCallback(async () => {
    setLoading(true);
    setError("");
    setEventDetails(null);
    try {
      const response = await getEventByToken(eventCode);

      if (response.status === "SUCCESS" && response.data) {
        const eventData = response.data;
        const cohostContact =
          getFirstPresent(
            eventData.cohost_contact,
            eventData.co_host_contact,
            eventData.cohostContact,
            eventData.cohost?.contact,
            eventData.cohost?.phone,
            eventData.cohost?.email,
            eventData.cohost?.name,
          ) || "";

        setEventDetails({
          title: eventData.title || "Event",
          status: getFirstPresent(eventData.status, eventData.event_status) || "",
          isCompleted: [eventData.status, eventData.event_status].some(
            (status) => typeof status === "string" && status.toLowerCase() === "completed",
          ),
          description:
            getFirstPresent(
              eventData.description,
              eventData.sub_title ? `Join us for ${eventData.sub_title}` : "",
            ) || "",
          date: formatEventDate(eventData),
          time: formatEventTime(
            getFirstPresent(eventData.event_time, eventData.time),
          ),
          location: getFirstPresent(eventData.location, eventData.city) || "",
          guestListSize:
            eventData.guest_list_size ??
            eventData.guestListSize ??
            eventData.guest_count ??
            0,
          eventImage: eventData.event_image || null,
          qrCode: eventData.qr_code || null,
          invitationLink: eventData.invitation_link || null,
          event_type: eventData.event_type || "",
          hostName: eventData.created_by?.name || "",
          hostUsername: eventData.created_by?.username || "",
          hostImage: eventData.created_by?.profile_pic || null,
          imageUploadDeadline: getDisplayValue(
            eventData.image_upload_deadline,
            eventData.imageUploadDeadline,
            eventData.upload_deadline,
            eventData.photo_upload_deadline,
            eventData.image_upload_end_at,
          ),
          revealDate: getDisplayValue(
            formatDateTime(eventData.reveal_datetime),
            eventData.reveal_date,
            eventData.revealDate,
            eventData.album_reveal_date,
            eventData.reveal_at,
          ),
          isAddCohost: Boolean(
            eventData.is_add_cohost ??
              eventData.isAddCohost ??
              eventData.add_cohost ??
              cohostContact,
          ),
          cohostContact,
        });
      } else {
        setError(
          response?.message ||
            response?.detail ||
            "This invitation could not be found or is no longer available.",
        );
      }
    } catch (err) {
      setError(
        getApiErrorMessage(
          err,
          "Unable to load this invitation. Please check the link and try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [eventCode]);

  useEffect(() => {
    fetchEventDetails();
  }, [fetchEventDetails]);

  const redirectToAuthForResponse = useCallback(
    (status) => {
      localStorage.setItem("eventCode", eventCode);
      localStorage.setItem(
        PENDING_INVITATION_RESPONSE_KEY,
        JSON.stringify({ eventCode, status }),
      );

      const redirectTo = encodeURIComponent(INVITATION_REDIRECT_PATH);
      const authToken = localStorage.getItem("authToken");
      const isCompleteAccount =
        localStorage.getItem("isCompleteAccount") === "true";

      if (authToken && !isCompleteAccount) {
        navigate(`/auth/complete-account?redirect-to=${redirectTo}`);
        return;
      }

      navigate(`/events/join/${eventCode}?redirect-to=${redirectTo}`);
    },
    [eventCode, navigate],
  );

  const ensureCanRespond = useCallback(
    (status) => {
      const authToken = localStorage.getItem("authToken");
      const isCompleteAccount =
        localStorage.getItem("isCompleteAccount") === "true";

      if (!authToken || !isCompleteAccount) {
        redirectToAuthForResponse(status);
        return false;
      }

      return true;
    },
    [redirectToAuthForResponse],
  );

  const submitInvitationResponse = useCallback(
    async (status) => {
      const responseState = RESPONSE_STATE_BY_STATUS[status];
      const errorMessage = RESPONSE_ERROR_BY_STATUS[status];

      if (!responseState) {
        return;
      }

      setAccepting(true);
      setError("");

      try {
        const response = await joinEvent(eventCode, status, "link");

        if (response.status === "SUCCESS") {
          localStorage.removeItem(PENDING_INVITATION_RESPONSE_KEY);
          setResponseState(responseState);
          setShowDownloadDialog(true);
        } else {
          localStorage.removeItem(PENDING_INVITATION_RESPONSE_KEY);
          setError(errorMessage);
          setAccepting(false);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          setAccepting(false);
          redirectToAuthForResponse(status);
          return;
        }

        localStorage.removeItem(PENDING_INVITATION_RESPONSE_KEY);
        setError(err.response?.data?.message || errorMessage);
        setAccepting(false);
      }
    },
    [eventCode, redirectToAuthForResponse],
  );

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    const isCompleteAccount =
      localStorage.getItem("isCompleteAccount") === "true";

    if (
      !eventDetails ||
      isEventCompleted ||
      !authToken ||
      !isCompleteAccount ||
      accepting ||
      responseState ||
      pendingResponseAttemptedRef.current
    ) {
      return;
    }

    const pendingResponse = localStorage.getItem(PENDING_INVITATION_RESPONSE_KEY);

    if (!pendingResponse) {
      return;
    }

    try {
      const { eventCode: pendingEventCode, status } =
        JSON.parse(pendingResponse);

      if (pendingEventCode === eventCode) {
        if (RESPONSE_STATE_BY_STATUS[status]) {
          pendingResponseAttemptedRef.current = true;
          submitInvitationResponse(status);
        } else {
          localStorage.removeItem(PENDING_INVITATION_RESPONSE_KEY);
        }
      }
    } catch (err) {
      localStorage.removeItem(PENDING_INVITATION_RESPONSE_KEY);
    }
  }, [accepting, eventCode, eventDetails, isEventCompleted, responseState, submitInvitationResponse]);

  const handleInvitationResponse = (status) => {
    if (!eventDetails || isEventCompleted) return;
    if (!ensureCanRespond(status)) {
      return;
    }

    submitInvitationResponse(status);
  };

  const handleAcceptInvitation = () => {
    handleInvitationResponse("accepted");
  };

  const handleDeclineInvitation = () => {
    handleInvitationResponse("maybe");
  };

  const handleRejectInvitation = () => {
    handleInvitationResponse("rejected");
  };

  const handleDownloadApp = () => {
    // Detect user's operating system and redirect to appropriate app store
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    let appStoreUrl;
    
    // Check for iOS/iPadOS
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      appStoreUrl = 'https://apps.apple.com/app/click';
    }
    // Check for Android
    else if (/android/i.test(userAgent)) {
      appStoreUrl = 'https://play.google.com/store/apps/details?id=com.canvasandcode.click';
    }
    // Default to App Store for desktop/other devices
    else {
      appStoreUrl = 'https://apps.apple.com/app/click';
    }
    
    // Open app store in new tab
    window.open(appStoreUrl, '_blank');
    // Then navigate to event join page
  };

  const handleReturnToEventJoin = () => {
    // Load a fresh event-join page, including when already on this URL.
    window.location.assign(`/events/join/${encodeURIComponent(eventCode)}`);
  };


  const handleSkipDownload = () => {
    // Show download app dialog
    setShowDownloadDialog(false);
  };

  const handleLocationClick = () => {
    if (eventDetails?.location && eventDetails.location !== EMPTY_VALUE) {
      // Open Google Maps with the location
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(eventDetails.location)}`;
      window.open(mapsUrl, '_blank');
    }
  };

  const renderEventSummary = () => {
    if (!eventDetails) {
      return null;
    }

    const hasLocation =
      eventDetails.location && eventDetails.location !== EMPTY_VALUE;
    const hasImageUploadDeadline =
      eventDetails.imageUploadDeadline &&
      eventDetails.imageUploadDeadline !== EMPTY_VALUE;
    const hasRevealDate =
      eventDetails.revealDate && eventDetails.revealDate !== EMPTY_VALUE;
    const guestListSize = eventDetails.guestListSize;
    const hasGuestListSize =
      guestListSize !== undefined &&
      guestListSize !== null &&
      guestListSize !== "" &&
      guestListSize !== 0;

    return (
      <div className="accepted-event-summary">
        <div className="event-summary-header">
          <div>
            <p className="event-summary-eyebrow">Event Summary</p>
            <h2>{eventDetails.title}</h2>
          </div>
        </div>

        <div className="event-summary-list">
          <div className="event-summary-item">
            <span className="event-summary-icon">📅</span>
            <span className="event-summary-text">
              {[eventDetails.date, eventDetails.time].filter(Boolean).join(" · ")}
            </span>
          </div>

          {hasLocation ? (
            <button
              type="button"
              className="event-summary-item event-summary-location"
              onClick={handleLocationClick}
            >
              <span className="event-summary-icon">📍</span>
              <span className="event-summary-text">
                {eventDetails.location}
              </span>
            </button>
          ) : null}

          {hasImageUploadDeadline && (
            <div className="event-summary-item">
              <span className="event-summary-icon">📸</span>
              <span className="event-summary-text">
                Image Upload Deadline - {eventDetails.imageUploadDeadline}
              </span>
            </div>
          )}

          {hasRevealDate && (
            <div className="event-summary-item">
              <span className="event-summary-icon">⏰</span>
              <span className="event-summary-text">
                Reveal Date - {eventDetails.revealDate}
              </span>
            </div>
          )}

          {eventDetails.isAddCohost && eventDetails.cohostContact && (
            <div className="event-summary-item">
              <span className="event-summary-icon">👤</span>
              <span className="event-summary-text">
                Co-host - {eventDetails.cohostContact}
              </span>
            </div>
          )}
        </div>

        {eventDetails.description && (
          <div className="event-summary-description">
            <p className="event-summary-description-label">Description</p>
            <p>{eventDetails.description}</p>
          </div>
        )}
      </div>
    );
  };

  if (loading && !eventDetails) {
    return (
      <>

        <div className="invitation-accept-container">
          <div className="invitation-accept-card">
            <div className="invitation-header">
              <div className="success-icon">✨</div>
              <h1>Loading Invitation...</h1>
              <p>Getting the event details ready.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error && !eventDetails) {
    return (
      <>

        <div className="invitation-accept-container">
          <div className="invitation-accept-card invitation-error-card">
            <div className="invitation-error-icon" aria-hidden="true">
              <span>!</span>
            </div>

            <div className="invitation-error-content">
              <span className="invitation-error-eyebrow">Invitation link</span>
              <h1>Invitation Unavailable</h1>
              <p className="invitation-error-intro">
                We couldn&apos;t open this event invitation.
              </p>

              <div className="invitation-error-detail" role="alert">
                <span className="invitation-error-detail-icon" aria-hidden="true">
                  i
                </span>
                <p>{error}</p>
              </div>

              <p className="invitation-error-help">
                Check that you&apos;re using the complete link, or ask the event
                host for a new invitation.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Show thank you message after user responds
  if (responseState === "accepted") {
    return (
      <>

        <div className="invitation-accept-container">
          <div className="invitation-accept-card">
            <div className="thank-you-header accepted">
              <div className="success-icon">🎉</div>
              <h1>Thank You!</h1>
              <p>We're excited to see you at {eventDetails?.title}</p>
            </div>

            <div className="thank-you-message">
              <p>
                You&apos;re officially on the guest list! Save the event details
                below—we can&apos;t wait to celebrate with you.
              </p>
            </div>

            {renderEventSummary()}

            <div className="invitation-actions">
              <button onClick={handleReturnToEventJoin} className="accept-btn" style={{width: 'auto'}}>
                Okay
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (responseState === "declined") {
    return (
      <>

        <div className="invitation-accept-container">
          <div className="invitation-accept-card">
            <div className="thank-you-header declined">
              <div className="success-icon">⏰</div>
              <h1>See You Soon!</h1>
              <p>Please accept the invitation before the event starts</p>
            </div>

            <div className="thank-you-message">
              <p>
                Thank you for your consideration. We hope you'll join us for{" "}
                {eventDetails?.title}!
              </p>
            </div>

            <div className="invitation-actions">
              <button onClick={handleReturnToEventJoin} className="accept-btn"style={{width: 'auto'}}>
                Okay
              </button>
            </div>
          </div>
        </div>

        {/* Download App Dialog */}
        {showDownloadDialog && (
          <div className="download-dialog-overlay">
            <div className="download-dialog">
              <div className="download-dialog-header">
                <div className="download-icon">📱</div>
                <h2>Join the Party Conversation!</h2>
                <p>Download the Click app to chat with other guests and share memories</p>
              </div>
              <div className="download-dialog-content">
                <div className="app-features">
                  <div className="feature">
                    <span className="feature-icon">💬</span>
                    <span>Chat with event guests</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">📸</span>
                    <span>Share photos and videos</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">🎉</span>
                    <span>Never miss a moment</span>
                  </div>
                </div>
              </div>
              <div className="download-dialog-actions">
                <button 
                  onClick={handleDownloadApp}
                  className="download-btn"
                >
                  Download App
                </button>
                <button 
                  onClick={handleSkipDownload}
                  className="skip-btn"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  if (responseState === "rejected") {
    return (
      <>

        <div className="invitation-accept-container">
          <div className="invitation-accept-card">
            <div className="thank-you-header declined">
              <div className="success-icon">😢</div>
              <h1>That's Too Bad!</h1>
              <p>We'll miss you, but hope to see you at the next event</p>
            </div>

            <div className="thank-you-message">
              <p>
                Thank you for considering our invitation. We hope you can join
                us at future events!
              </p>
            </div>
          </div>
        </div>

        {/* Download App Dialog */}
        {showDownloadDialog && (
          <div className="download-dialog-overlay">
            <div className="download-dialog">
              <div className="download-dialog-header">
                <div className="download-icon">📱</div>
                <h2>Join the Party Conversation!</h2>
                <p>Download the Click app to chat with other guests and share memories</p>
              </div>
              <div className="download-dialog-content">
                <div className="app-features">
                  <div className="feature">
                    <span className="feature-icon">💬</span>
                    <span>Chat with event guests</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">📸</span>
                    <span>Share photos and videos</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">🎉</span>
                    <span>Never miss a moment</span>
                  </div>
                </div>
              </div>
              <div className="download-dialog-actions">
                <button 
                  onClick={handleDownloadApp}
                  className="download-btn"
                >
                  Download App
                </button>
                <button 
                  onClick={handleSkipDownload}
                  className="skip-btn"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>

      <div className="invitation-accept-container">
        <div className="invitation-accept-card">
          <div className="invitation-header">
            {!isEventCompleted && <div className="success-icon">✨</div>}
            <h1>{isEventCompleted ? "Event completed" : "You're Invited!"}</h1>
            <p>{isEventCompleted ? "This event has ended. Responses are now closed." : "Capture memories! 📸 This event will be legendary! 🎉"}</p>
          </div>
          {eventDetails && (
            <div className="event-details">
              {eventDetails.eventImage && (
                <div className="event-image-container">
                  <img
                    src={eventDetails.eventImage}
                    alt={eventDetails.title}
                    className="event-image"
                  />
                </div>
              )}
              <div className="event-details-content">
                {eventDetails.status && (
                  <span className="invitation-event-status">
                    {eventDetails.status.replace(/_/g, " ")}
                  </span>
                )}
                <h2>{eventDetails.title}</h2>
                {eventDetails.description && (
                  <p className="event-description">{eventDetails.description}</p>
                )}

                {eventDetails.hostName && (
                  <div className="event-host">
                    {eventDetails.hostImage ? (
                      <img
                        src={eventDetails.hostImage}
                        alt=""
                        className="event-host-avatar"
                      />
                    ) : (
                      <span className="event-host-avatar event-host-avatar-fallback">
                        {eventDetails.hostName.charAt(0).toUpperCase()}
                      </span>
                    )}
                    <div>
                      <span className="event-host-label">Hosted by</span>
                      <span className="event-host-name">
                        {eventDetails.hostName}
                        {eventDetails.hostUsername && ` · @${eventDetails.hostUsername}`}
                      </span>
                    </div>
                  </div>
                )}

                <div className="event-meta">
                  {eventDetails.date && (
                    <div className="meta-item">
                      <span className="meta-icon">📅</span>
                      <span>{eventDetails.date}</span>
                    </div>
                  )}
                  {eventDetails.time && (
                    <div className="meta-item">
                      <span className="meta-icon">⏰</span>
                      <span>{eventDetails.time}</span>
                    </div>
                  )}
                  {eventDetails.location && (
                    <div className="meta-item clickable-location" onClick={handleLocationClick}>
                      <span className="meta-icon">📍</span>
                      <span className="location-text">{eventDetails.location}</span>
                    </div>
                  )}
                  {eventDetails.revealDate &&
                    eventDetails.revealDate !== EMPTY_VALUE && (
                      <div className="meta-item">
                        <span className="meta-icon">🖼️</span>
                        <span>Photos reveal {eventDetails.revealDate}</span>
                      </div>
                    )}
                </div>

                {/* {eventDetails.qrCode && (
                  <div className="event-qr-container">
                    <p className="qr-label">Event QR Code</p>
                    <img
                      src={eventDetails.qrCode}
                      alt="Event QR Code"
                      className="event-qr-code"
                    />
                  </div>
                )} */}
              </div>
            </div>
          )}

          {!isEventCompleted && (eventDetails?.event_type === "community" ? <div>
              <button
              onClick={handleAcceptInvitation}
              disabled={accepting}
              className="accept-btn accept-btn-green-text"
            >
              {"Yes, I would like to attend"}
            </button>
            </div> : (
            <div className="invitation-actions">
            <button
              onClick={handleAcceptInvitation}
              disabled={accepting}
              className="round-button accept-btn accept-btn-green flex-button"
            >
              {"✓"}
            </button>

            <button
              onClick={handleDeclineInvitation}
              disabled={accepting}
              className="round-button maybe-btn maybe-btn-yellow flex-button"
            >
              ?
            </button>

            <button
              onClick={handleRejectInvitation}
              disabled={accepting}
              className="round-button reject-btn flex-button"
            >
              X
            </button>
          </div>
          ))}
          {error && <div className="error-message">{error}</div>}

          <div className="invitation-footer">
            <p>
              Event Code: <strong>{eventCode}</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Download App Dialog */}
      {showDownloadDialog && (
        <div className="download-dialog-overlay">
          <div className="download-dialog">
            <div className="download-dialog-header">
              <div className="download-icon">📱</div>
              <h2>Join the Party Conversation!</h2>
              <p>Download the Click app to chat with other guests and share memories</p>
            </div>
            <div className="download-dialog-content">
              <div className="app-features">
                <div className="feature">
                  <span className="feature-icon">💬</span>
                  <span>Chat with event guests</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">📸</span>
                  <span>Share photos and videos</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">🎉</span>
                  <span>Never miss a moment</span>
                </div>
              </div>
            </div>
            <div className="download-dialog-actions">
              <button 
                onClick={handleDownloadApp}
                className="download-btn"
              >
                Download App
              </button>
              <button 
                onClick={handleSkipDownload}
                className="skip-btn"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InvitationAccept;
